/**
 * GS1 Data Matrix — İTS / eczane karekodu (tek parser: takas, satış, doğrulama).
 *
 * Desteklenen AI’lar: 01 (GTIN), 21 (seri), 17 (SKT YYMMDD), 10 (parti). Ayırıcı: GS \x1d veya tab.
 * Seri (21) değişken uzunluk: biter — ayırıcı, veya geçerli 17+YYMMDD, veya 10 parti.
 */

export interface DataMatrixResult {
  gtin: string | null;
  serialNumber: string | null;
  expiryDate: string | null;
  lotNumber: string | null;
  rawData: string;
  isValid: boolean;
  errors: string[];
}

function trimDash(s: string): string {
  return s.replace(/^-+|-+$/g, '').trim();
}

/** GS1 alan ayırıcısı (FNC1). JavaScript'te `\s` bu karakteri kapsamaz, elle temizlenir. */
const GRUP_AYIRICI = '\x1d';
/** Okuyucunun eklediği simgeleme öneki: `]d2` (Data Matrix / GS1), `]C1`, `]E0`, `]Q3` … */
const SIMGELEME_ONEKI = /^\][A-Za-z][0-9]/;
/** Kodun başındaki ve sonundaki ayırıcılar veriye ait değildir. */
const KENAR_AYIRICILAR = /^[\x1d\t]+|[\x1d\t]+$/g;
/** Bazı çözücüler / UI ayırıcıyı görünür metne çevirir. */
const YAZILI_AYIRICI = /<GS>|\{GS\}|⟨GS⟩|\(GS\)|\u241d/gi;

/**
 * Aynı kutu el terminaliyle de kamerayla da okutulduğunda aynı metni versin.
 *
 * El terminalleri baştaki FNC1'i hiç göndermez; kamera çözücüleri ise onu `\x1d` olarak
 * veya `]d2` simgeleme öneki biçiminde iletir. Temizlenmezse GTIN arayan kontroller
 * başarısız olur ve aynı kutu iki ayrı okuma sayılır.
 */
export function normalizeGs1Raw(rawData: string): string {
  if (!rawData) return '';
  let text = rawData.replace(YAZILI_AYIRICI, GRUP_AYIRICI).replace(/[\r\n\s]/g, '');
  text = text.replace(KENAR_AYIRICILAR, '');
  text = text.replace(SIMGELEME_ONEKI, '');
  return text.replace(KENAR_AYIRICILAR, '');
}

function isValidYyMmDd(yyMMDD: string): boolean {
  if (!/^\d{6}$/.test(yyMMDD)) return false;
  const mm = parseInt(yyMMDD.slice(2, 4), 10);
  const dd = parseInt(yyMMDD.slice(4, 6), 10);
  if (mm < 1 || mm > 12) return false;
  if (dd < 1 || dd > 31) return false;
  return true;
}

function yyMmDdToIso(yyMMDD: string): string {
  return `20${yyMMDD.slice(0, 2)}-${yyMMDD.slice(2, 4)}-${yyMMDD.slice(4, 6)}`;
}

/**
 * Değişken alanın bittiği index (21 veya 10 gövdesi için).
 *
 * `partiBaslangiciAra` yalnızca seri (21) okunurken açılır. Ayırıcısız kodlarda serinin nerede
 * bittiğini anlamanın tek yolu bir sonraki alanı aramaktır; İTS düzeninde bu 17, o yoksa 10'dur.
 * Parti (10) ise düzenin son alanı olduğu için sonuna kadar okunur: içinde «10» geçen bir parti
 * numarası (ör. 24096010A) aksi hâlde ikiye bölünüyor, kalan «10A» ikinci bir parti alanı
 * sanılıyor ve parti numarası «A» olarak yazılıyordu.
 */
function findVariableFieldEnd(s: string, start: number, partiBaslangiciAra: boolean): number {
  for (let k = start; k < s.length; k++) {
    const c = s[k];
    if (c === '\x1d' || c === '\u001d' || c === '\t') return k;
  }
  let best = s.length;
  for (let j = start; j <= s.length - 8; j++) {
    const t = s.slice(j);
    if (!t.startsWith('17')) continue;
    const six = t.slice(2, 8);
    if (/^\d{6}$/.test(six) && isValidYyMmDd(six)) best = Math.min(best, j);
  }
  if (best < s.length) return best;
  if (!partiBaslangiciAra) return s.length;
  for (let j = start + 1; j < s.length - 1; j++) {
    if (s.slice(j, j + 2) === '10') return j;
  }
  return s.length;
}

function parseApplicationIdentifiers(rest: string): {
  serialNumber: string | null;
  expiryDate: string | null;
  lotNumber: string | null;
  errors: string[];
} {
  const errors: string[] = [];
  let serialNumber: string | null = null;
  let expiryDate: string | null = null;
  let lotNumber: string | null = null;
  let i = 0;
  const r = rest;

  while (i < r.length) {
    const c = r[i];
    if (c === '\x1d' || c === '\u001d' || c === '\t') {
      i += 1;
      continue;
    }
    const tail = r.slice(i);

    if (tail.startsWith('17') && tail.length >= 8 && /^\d{6}/.test(tail.slice(2, 8))) {
      const raw = tail.slice(2, 8);
      if (isValidYyMmDd(raw)) expiryDate = yyMmDdToIso(raw);
      else errors.push('Geçersiz SKT (YYMMDD)');
      i += 8;
      continue;
    }
    if (tail.startsWith('21')) {
      i += 2;
      const end = findVariableFieldEnd(r, i, true);
      const value = trimDash(r.slice(i, end));
      if (value) serialNumber = value;
      i = end;
      continue;
    }
    if (tail.startsWith('10')) {
      i += 2;
      const end = findVariableFieldEnd(r, i, false);
      const value = trimDash(r.slice(i, end));
      if (value) lotNumber = value;
      i = end;
      continue;
    }
    i += 1;
  }

  return { serialNumber, expiryDate, lotNumber, errors };
}

export function parseDataMatrix(rawData: string): DataMatrixResult {
  const result: DataMatrixResult = {
    gtin: null,
    serialNumber: null,
    expiryDate: null,
    lotNumber: null,
    rawData,
    isValid: false,
    errors: [],
  };

  if (!rawData || rawData.trim().length === 0) {
    result.errors.push('Karekod verisi boş');
    return result;
  }

  try {
    const cleanCode = normalizeGs1Raw(rawData);

    if (!cleanCode.startsWith('01') || cleanCode.length < 15) {
      result.errors.push('GTIN bulunamadı (AI: 01)');
      return result;
    }

    const after01 = cleanCode.slice(2);
    const gtinMatch = after01.match(/^(\d{14})/) ?? after01.match(/^(\d{13})(?:\D|$)/);
    if (!gtinMatch) {
      result.errors.push('Geçersiz GTIN formatı (14 digit gerekli)');
      return result;
    }

    result.gtin = gtinMatch[1].length === 13 ? '0' + gtinMatch[1] : gtinMatch[1];
    const gtinConsumed = gtinMatch[1].length;
    const rest = cleanCode.slice(2 + gtinConsumed);

    const parsed = parseApplicationIdentifiers(rest);
    result.serialNumber = parsed.serialNumber;
    result.expiryDate = parsed.expiryDate;
    result.lotNumber = parsed.lotNumber;
    for (const e of parsed.errors) {
      if (!result.errors.includes(e)) result.errors.push(e);
    }
    result.isValid = !!result.gtin;
  } catch (error) {
    result.errors.push(`Parse hatası: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`);
  }

  return result;
}

export function isDataMatrix(input: string): boolean {
  if (!input?.trim()) return false;
  const clean = normalizeGs1Raw(input);
  if (clean.length < 15) return false;
  return (
    clean.startsWith('01') &&
    (clean.includes('21') || clean.includes('17') || clean.includes('10'))
  );
}

export function isSimpleBarcode(input: string): boolean {
  if (!input) return false;
  return /^\d{13,14}$/.test(normalizeGs1Raw(input));
}

export function extractGTIN(input: string): string | null {
  if (!input) return null;
  if (isDataMatrix(input)) return parseDataMatrix(input).gtin;
  if (isSimpleBarcode(input)) {
    const cleaned = normalizeGs1Raw(input);
    return cleaned.length === 13 ? '0' + cleaned : cleaned;
  }
  return null;
}

/** Enter ile işlenmeli mi: okuyucu GS1 / EAN; listeden seçimle karışmasın. */
export function looksLikeBarcodeOrGs1Input(raw: string): boolean {
  if (!raw.trim()) return false;
  const clean = normalizeGs1Raw(raw);
  if (clean.startsWith('01') && clean.length >= 15) return true;
  if (/^\d{13,14}$/.test(clean)) return true;
  return false;
}

export function formatDataMatrixResult(result: DataMatrixResult): string {
  const parts: string[] = [];
  if (result.gtin) parts.push(`GTIN: ${result.gtin}`);
  if (result.serialNumber) parts.push(`Seri: ${result.serialNumber}`);
  if (result.expiryDate) parts.push(`SKT: ${result.expiryDate}`);
  if (result.lotNumber) parts.push(`Lot: ${result.lotNumber}`);
  return parts.join(' | ');
}

/**
 * SKT’yi GS1 AI 17 (YYMMDD) yapar.
 * ISO tarih (`2029-07-31`), saatli ISO (`2029-07-31T00:00:00`), portal `30-08-2025`,
 * `31.07.2029` ve düz 6/8 hane kabul edilir. Saat haneleri yutulmaz; aksi halde
 * karekod SKT’siz kurulur (01+21+10).
 */
export function gs1SktYyMmDd(xdRaw: string | Date | null | undefined): string | null {
  if (xdRaw instanceof Date && !Number.isNaN(xdRaw.getTime())) {
    const six =
      String(xdRaw.getFullYear()).slice(2) +
      String(xdRaw.getMonth() + 1).padStart(2, '0') +
      String(xdRaw.getDate()).padStart(2, '0')
    return isValidYyMmDd(six) ? six : null
  }

  const ham = String(xdRaw ?? '').trim()
  if (!ham) return null

  const iso = ham.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (iso) {
    const six = iso[1].slice(2) + iso[2] + iso[3]
    return isValidYyMmDd(six) ? six : null
  }

  const gunAyYil = ham.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})/)
  if (gunAyYil) {
    const six =
      gunAyYil[3].slice(2) + gunAyYil[2].padStart(2, '0') + gunAyYil[1].padStart(2, '0')
    return isValidYyMmDd(six) ? six : null
  }

  const rakamlar = ham.replace(/\D/g, '')
  if (rakamlar.length >= 8 && (rakamlar.startsWith('19') || rakamlar.startsWith('20'))) {
    const six = rakamlar.slice(2, 8)
    return isValidYyMmDd(six) ? six : null
  }
  if (rakamlar.length === 8) {
    const six = rakamlar.slice(2)
    return isValidYyMmDd(six) ? six : null
  }
  if (rakamlar.length === 6) return isValidYyMmDd(rakamlar) ? rakamlar : null
  return null
}

/** ITS alanlarından karekod üretir — okuyucu / Notepad gibi yapışık: 01→21→17→10 (GS yok). */
export function buildDataMatrixFromIts(its: {
  GTIN?: string;
  gtin?: string;
  SN?: string;
  sn?: string;
  XD?: string | Date | null;
  xd?: string | Date | null;
  BN?: string;
  bn?: string;
}): string {
  const gtin = (its.GTIN ?? its.gtin ?? '').trim();
  const gtin14 = gtin.length === 13 ? '0' + gtin : gtin.length === 14 ? gtin : gtin ? gtin.padStart(14, '0').slice(-14) : '';
  if (!gtin14 || gtin14.length !== 14) return '';

  const xd6 = gs1SktYyMmDd(its.XD ?? its.xd);
  const hasSkt = Boolean(xd6);

  const sn = (its.SN ?? its.sn ?? '').trim();
  const bn = (its.BN ?? its.bn ?? '').trim();

  let kod = '01' + gtin14;
  if (sn) kod += '21' + sn;
  if (hasSkt) kod += '17' + xd6;
  if (bn) {
    // Seri var, SKT yok: değişken seri bitişini ayırmak için tek GS (kutudanKarekodKur ile aynı).
    if (sn && !hasSkt) kod += GRUP_AYIRICI;
    kod += '10' + bn;
  }

  return kod;
}

/**
 * Ekranda gösterilecek karekod metni — okuyucu gibi yapışık.
 * Eski kayıtlardaki GS (\x1d) tofu basmasın diye gösterimde çıkarılır; üretim artık GS koymaz.
 */
export function formatDataMatrixForDisplay(raw: string): string {
  if (!raw) return '';
  return raw.replace(/\u001d/g, '');
}

export type Gs1GosterimParcasi = { tur: 'ai' | 'deger'; metin: string }

/** Ham GS1 karekod (01 + 14 hane ve devamı). Düz barkod / GTIN boyanmaz. */
export function gs1HamKarekodMu(raw: string): boolean {
  const s = formatDataMatrixForDisplay(raw)
  return /^01\d{14}/.test(s) && s.length >= 16
}

/** Ekranda 01 / 21 / 17 / 10 ayraçlarını boyamak için parçalar. Kopya metni değişmez. */
export function gs1GosterimParcalari(raw: string): Gs1GosterimParcasi[] {
  const s = formatDataMatrixForDisplay(raw)
  if (!s) return []
  if (!gs1HamKarekodMu(s)) return [{ tur: 'deger', metin: s }]

  const after01 = s.slice(2)
  const gtinMatch = after01.match(/^(\d{14})/)
  if (!gtinMatch) return [{ tur: 'deger', metin: s }]

  const parcalar: Gs1GosterimParcasi[] = [
    { tur: 'ai', metin: '01' },
    { tur: 'deger', metin: gtinMatch[1] },
  ]
  const rest = s.slice(2 + gtinMatch[1].length)
  let i = 0
  while (i < rest.length) {
    const c = rest[i]
    if (c === '\x1d' || c === '\t') {
      i += 1
      continue
    }
    const tail = rest.slice(i)
    if (tail.startsWith('17') && tail.length >= 8 && isValidYyMmDd(tail.slice(2, 8))) {
      parcalar.push({ tur: 'ai', metin: '17' })
      parcalar.push({ tur: 'deger', metin: tail.slice(2, 8) })
      i += 8
      continue
    }
    if (tail.startsWith('21')) {
      const end = findVariableFieldEnd(rest, i + 2, true)
      parcalar.push({ tur: 'ai', metin: '21' })
      parcalar.push({ tur: 'deger', metin: rest.slice(i + 2, end) })
      i = end
      continue
    }
    if (tail.startsWith('10')) {
      const end = findVariableFieldEnd(rest, i + 2, false)
      parcalar.push({ tur: 'ai', metin: '10' })
      parcalar.push({ tur: 'deger', metin: rest.slice(i + 2, end) })
      i = end
      continue
    }
    parcalar.push({ tur: 'deger', metin: rest.slice(i) })
    break
  }
  return parcalar
}

/** Aynı GTIN: çoklu kutu satır satır; kısmi + tam okumada (prefix) tek tam satır kalsın. */
export function mergeDataMatrixRawLines(existing: string | undefined, incoming: string | undefined): string | undefined {
  const a = existing?.trim();
  const b = incoming?.trim();
  if (!b) return a || undefined;
  if (!a) return b;
  if (a === b) return a;
  if (b.startsWith(a)) return b;
  if (a.startsWith(b)) return a;
  const linesA = a.split(/\r?\n/);
  if (linesA.includes(b)) return a;
  return `${a}\n${b}`;
}
